import React, { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    BudgetProposalT,
    ResourceSoftwareIdField,
} from 'routes/BudgetModule/config/types';
import { BudgetExpenseOrIncome } from 'business/constants';
import { useTranslation } from 'react-i18next';
import { getResourceSoftwareIdField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import {
    getProjectionDetailsUpdateTracker,
    getTrackEventsForAtterrissage,
} from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/trackerHelpers';
import { ExcelExportConfig } from 'routes/BudgetModule/contexts/GridApiContext';
import { getAtterrissageColDefs } from '../helpers/colDefinitions';
import { useGetAtterrissageProjectionDetails } from '../hooks/atterrissage';
import ProjectionGrid, { ProjectionGridWrapper } from './ProjectionGrid';
import { CellRendererParamsProjectionStatusAndSidePanelT } from './ProjectionStatusSidePanelCellRenderer';
import { AtterrissageProjectionDetailsCellRendererCustomParams } from './AtterrissageProjectionDetailCellRenderer';
import { useGetProjectionFilters } from '../hooks/projectionFilters';
import { useBuildUnicityDimensions } from '../hooks/gridUnicityDimensions';
import { useGetProjectionDetailsWithExtraFields } from '../hooks/projectionDetails';
import { getProjectionsDimensionMapping } from '../helpers/dataHelpers';
import { useIntegrationSoftwares } from 'helpers/integrations';

type AtterrissageGridProps = {
    budgetProposal: BudgetProposalT;
    resourceSoftwareId?: string;
    expenseOrIncome: BudgetExpenseOrIncome;
    code?: string;
    isDaf?: boolean;
    excelExport?: ExcelExportConfig;
    shouldShowOpenedGroups?: boolean;
};

const AtterrissageGrid = ({
    budgetProposal,
    resourceSoftwareId,
    expenseOrIncome,
    code,
    isDaf = false,
    excelExport = undefined,
    shouldShowOpenedGroups = false,
}: AtterrissageGridProps): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        by,
        exercice,
        id: budgetProposalId,
        projectionBy: projectionBySoftwareIdArray,
    } = budgetProposal;

    const resourceSoftwareIdField: ResourceSoftwareIdField =
        getResourceSoftwareIdField(by);

    const {
        error,
        isLoading: isProjectionDetailsLoading,
        data: projectionDetailsWithProjections,
        ventilationsLabelMapping,
    } = useGetProjectionDetailsWithExtraFields({
        budgetProposal,
        resourceSoftwareId,
        expenseOrIncome,
        resourceSoftwareIdField,
        isDaf,
        useGetProjectionDetailsHook: useGetAtterrissageProjectionDetails,
    });

    const { filteredProjectionsDetails, filters } = useGetProjectionFilters(
        projectionDetailsWithProjections,
        isProjectionDetailsLoading,
        resourceSoftwareIdField,
        projectionBySoftwareIdArray,
        ventilationsLabelMapping,
        code,
    );

    const dimensionsMapping = getProjectionsDimensionMapping(
        resourceSoftwareIdField,
        projectionBySoftwareIdArray,
        ventilationsLabelMapping,
        projectionDetailsWithProjections,
    );

    const projectionDetailsUpdateTracker = getProjectionDetailsUpdateTracker({
        isDaf,
        resourceSoftwareIdNameMapping:
            dimensionsMapping[resourceSoftwareIdField]?.mapping,
        budgetProposal,
    });
    const trackEvent = getTrackEventsForAtterrissage(isDaf);

    const cellRendererCustomParams: AtterrissageProjectionDetailsCellRendererCustomParams =
        {
            resourceSoftwareId,
            projectionDetailsUpdateTracker,
            dimensionsMapping,
            projectionIdLabelMapping: ventilationsLabelMapping!,
        };
    const statusCellRendererCustomParams: CellRendererParamsProjectionStatusAndSidePanelT =
        {
            budgetProposalId,
            exercice,
            resourceSoftwareId,
            dimensionsMapping,
            projectionIdLabelMapping: ventilationsLabelMapping!,
        };

    const { data: softwares } = useIntegrationSoftwares();

    const colDefs = useMemo(
        () =>
            getAtterrissageColDefs({
                t,
                theme,
                exercice,
                cellRendererCustomParams,
                statusCellRendererCustomParams,
                updateProjectionParams: {
                    budgetProposal,
                    resourceSoftwareIdField,
                    expenseOrIncome,
                    isDaf,
                },
                isDaf,
                dimensionsMapping,
                budgetProposal,
                softwares:
                    softwares === undefined ? new Set<string>() : softwares,
            }),
        [
            t,
            theme,
            exercice,
            JSON.stringify(cellRendererCustomParams),
            JSON.stringify(statusCellRendererCustomParams),
            resourceSoftwareIdField,
            JSON.stringify(dimensionsMapping),
            isDaf,
            softwares,
        ],
    );

    const { unicityDimensions } = useBuildUnicityDimensions(
        resourceSoftwareIdField,
        budgetProposal.projectionBy,
    );

    return (
        <ProjectionGridWrapper
            isLoading={isProjectionDetailsLoading}
            isDaf={isDaf}
            error={error}
        >
            <ProjectionGrid
                budgetProposal={budgetProposal}
                colDefs={colDefs}
                unicityDimensions={unicityDimensions}
                filters={filters}
                projectionsDetails={filteredProjectionsDetails!}
                isDaf={isDaf}
                trackEvents={trackEvent}
                excelExport={excelExport}
                shouldShowOpenedGroups={shouldShowOpenedGroups}
            />
        </ProjectionGridWrapper>
    );
};

export default memo(AtterrissageGrid);
